/**
 * Copyright (c) 2024-2026 Ronan LE MEILLAT
 * License: AGPL-3.0-or-later
 */

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Chip, Modal } from "@heroui/react";
import { Trash2, Plus, Copy } from "lucide-react";

import { useSecuredApi } from "@/authentication";
import { getApiBase } from "@/lib/api-base";
import { ImageUploadInput } from "@/components/image-upload-input";

interface VariantImage {
  id: string;
  url: string;
  kind: "worn" | "gallery";
  position: number;
}

interface Props {
  productId: string;
  variantId: string;
  variantTitle: string;
}

/**
 * Manages the worn/gallery image gallery for a single variant.
 * Mirrors the structure of `variant-prices.tsx`: fetch on mount, optimistic
 * local state updates, no global cache.
 */
export function VariantImages({ productId, variantId, variantTitle }: Props) {
  const { t } = useTranslation();
  const { getJson, postJson, postForm, deleteJson } = useSecuredApi();
  const apiBase = getApiBase();

  const [images, setImages] = useState<VariantImage[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState<string | null>(null);
  const [newKind, setNewKind] = useState<"worn" | "gallery">("worn");
  const [submitting, setSubmitting] = useState(false);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  useEffect(() => {
    loadImages();
  }, [variantId]);

  const loadImages = async () => {
    try {
      const res = await getJson(`${apiBase}/v1/products/${productId}/variants/${variantId}/images`);
      setImages(res.items || []);
    } catch (err) {
      console.error("Failed to load variant images", err);
    }
  };

  const handleAdd = async () => {
    if (!newImageUrl) return;
    setSubmitting(true);
    try {
      const created = await postJson(
        `${apiBase}/v1/products/${productId}/variants/${variantId}/images`,
        { url: newImageUrl, kind: newKind, position: images.length },
      );
      setImages([...images, created]);
      setIsAddModalOpen(false);
      setNewImageUrl(null);
      setNewKind("worn");
    } catch (err) {
      console.error("Failed to add variant image", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (imageId: string) => {
    if (!confirm(t("admin-variant-images-confirm-delete"))) return;
    try {
      await deleteJson(`${apiBase}/v1/products/${productId}/variants/${variantId}/images/${imageId}`);
      setImages(images.filter((img) => img.id !== imageId));
    } catch (err) {
      console.error("Failed to delete variant image", err);
    }
  };

  const handleCopyToColor = async () => {
    try {
      const result = await postJson(
        `${apiBase}/v1/products/${productId}/variants/${variantId}/images/copy-to-color`,
        {},
      );
      setCopyMessage(
        result.updated > 0
          ? t("admin-variant-images-copy-success", { count: result.updated })
          : t("admin-variant-images-copy-none"),
      );
    } catch (err) {
      console.error("Failed to copy images to color", err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">{t("admin-variant-images-title")}</h3>
          <p className="text-xs text-default-500">
            {t("admin-variant-images-subtitle")} {variantTitle}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onPress={handleCopyToColor} isDisabled={images.length === 0}>
            <Copy className="w-4 h-4" />
            {t("admin-variant-images-copy-to-color")}
          </Button>
          <Button onPress={() => setIsAddModalOpen(true)}>
            <Plus className="w-4 h-4" />
            {t("admin-variant-images-add")}
          </Button>
        </div>
      </div>

      {copyMessage && <p className="text-xs text-default-600">{copyMessage}</p>}

      {images.length === 0 ? (
        <Card>
          <Card.Content className="text-center text-sm text-default-500 py-6">
            {t("admin-variant-images-empty")}
          </Card.Content>
        </Card>
      ) : (
        <div className="grid grid-cols-4 gap-3">
          {images.map((img) => (
            <div key={img.id} className="relative">
              <div className="aspect-square bg-white rounded-lg overflow-hidden border border-default-200">
                <img src={img.url} alt="" className="w-full h-full object-cover" />
              </div>
              <Chip size="sm" className="absolute top-1 left-1" variant="soft" color={img.kind === "worn" ? "accent" : "default"}>
                {img.kind === "worn" ? t("admin-variant-images-kind-worn") : t("admin-variant-images-kind-gallery")}
              </Chip>
              <Button
                isIconOnly
                size="sm"
                variant="tertiary"
                className="absolute top-1 right-1 text-red-600"
                aria-label="Delete image"
                onPress={() => handleDelete(img.id)}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <Modal.Backdrop>
          <Modal.Container>
            <Modal.Dialog>
              {({ close }) => (
                <>
                  <Modal.CloseTrigger onPress={close} />
                  <Modal.Header>{t("admin-variant-images-add-title")}</Modal.Header>
                  <Modal.Body>
                    <div className="space-y-4">
                      <p className="text-sm text-default-600">{variantTitle}</p>

                      <div>
                        <label className="text-sm font-medium">{t("admin-variant-images-select-kind")}</label>
                        <div className="flex gap-2 mt-1">
                          <Button
                            size="sm"
                            variant={newKind === "worn" ? "primary" : "outline"}
                            onPress={() => setNewKind("worn")}
                          >
                            {t("admin-variant-images-kind-worn")}
                          </Button>
                          <Button
                            size="sm"
                            variant={newKind === "gallery" ? "primary" : "outline"}
                            onPress={() => setNewKind("gallery")}
                          >
                            {t("admin-variant-images-kind-gallery")}
                          </Button>
                        </div>
                      </div>

                      <ImageUploadInput
                        value={newImageUrl}
                        onChange={setNewImageUrl}
                        apiBaseUrl={apiBase}
                        postForm={postForm}
                      />
                    </div>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="tertiary" onPress={close}>
                      {t("admin-common-cancel")}
                    </Button>
                    <Button isDisabled={!newImageUrl || submitting} isPending={submitting} onPress={handleAdd}>
                      {t("admin-common-save")}
                    </Button>
                  </Modal.Footer>
                </>
              )}
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </div>
  );
}
